import { EventPartsQuery, GetLiveScheduleQuery } from "@src/@types/api.d";
import useStore from "@src/store";
import { parseEventDate } from "@src/utils/dateFormatter";
import { useMemo } from "react";

export type EventPartViewModel = EventPartsQuery["eventParts"][number] & {
  key: string;
  effectiveTimeZone?: string | null;
  formattedDate: string;
  formattedTime: string;
  timeZoneLabel: string | null;
  hasLiveStream: boolean;
  showAddress: boolean;
  showCalendarAction: boolean;
  showMapAction: boolean;
  latitude: number | null;
  longitude: number | null;
};

type UseEventPartsInput = {
  eventPartsData?: EventPartsQuery;
  liveSchedules?: GetLiveScheduleQuery;
  eventTimezone?: string | null;
  isInvitedToEvent?: boolean;
};

function formatDateInTimeZone(dateTime: string, timeZone?: string | null) {
  const date = parseEventDate(dateTime);

  if (!date) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    return date.toDateString();
  }
}

function formatTimeInTimeZone(dateTime: string, timeZone?: string | null) {
  const date = parseEventDate(dateTime);

  if (!date) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}

function formatTimeZoneLabel(dateTime: string, timeZone?: string | null) {
  const date = parseEventDate(dateTime);

  if (!timeZone || !date) {
    return null;
  }

  const fallbackLabel = timeZone.replace(/_/g, " ");

  try {
    const timeZoneName = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;

    return timeZoneName ? `${timeZoneName} (${fallbackLabel})` : fallbackLabel;
  } catch {
    return fallbackLabel;
  }
}

function toCoordinate(value?: string | null) {
  if (!value) {
    return null;
  }

  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}

export const useEventParts = ({
  eventPartsData,
  liveSchedules,
  eventTimezone,
  isInvitedToEvent = false,
}: UseEventPartsInput) => {
  const isPreviewMode = useStore((state) => state.isPreviewMode);

  const parts = useMemo<EventPartViewModel[]>(() => {
    const liveScheduleIds = new Set(
      (liveSchedules?.getLiveSchedule?.data ?? [])
        .map((schedule) => schedule?.event_part?.id)
        .filter((id): id is number => id != null)
    );

    return (eventPartsData?.eventParts ?? []).map((item, index) => {
      const effectiveTimeZone = item.eventPartTimezone ?? eventTimezone;
      const latitude = toCoordinate(item.geo_position?.latitude);
      const longitude = toCoordinate(item.geo_position?.longitude);
      const hasCoordinates = latitude != null && longitude != null;

      return {
        ...item,
        key: `event-part-${item.id ?? index}`,
        effectiveTimeZone,
        formattedDate: formatDateInTimeZone(item.time, effectiveTimeZone),
        formattedTime: formatTimeInTimeZone(item.time, effectiveTimeZone),
        timeZoneLabel: formatTimeZoneLabel(item.time, effectiveTimeZone),
        hasLiveStream: liveScheduleIds.has(item.id),
        showAddress: isInvitedToEvent || isPreviewMode,
        showCalendarAction: isInvitedToEvent || isPreviewMode,
        showMapAction:
          (isInvitedToEvent || isPreviewMode) &&
          !!item.geo_position?.geo_position_id &&
          hasCoordinates,
        latitude,
        longitude,
      };
    });
  }, [
    eventPartsData,
    eventTimezone,
    isInvitedToEvent,
    isPreviewMode,
    liveSchedules,
  ]);

  return {
    parts,
    hasEventParts: parts.length > 0,
  };
};
